import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { client } from "@/lib/api";
import { COMMON_TRIGGERS } from "@/data/content";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { ResponsiveModal } from "@/components/ui/responsive-modal";
import { NotebookPen, Plus, TrendingUp, Flame, Check, X, Lightbulb } from "lucide-react";

function timeAgo(iso) {
  const then = new Date(iso).getTime();
  if (!then) return "";
  const s = Math.floor((Date.now() - then) / 1000);
  if (s < 60) return "just now";
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  return `${d}d ago`;
}

const DOTS = [1, 2, 3, 4, 5];

export default function CravingJournal() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [intensity, setIntensity] = useState(3);
  const [trigger, setTrigger] = useState("");
  const [coped, setCoped] = useState("");
  const [saving, setSaving] = useState(false);

  const load = () => client.get("/cravings").then((r) => setItems(r.data || [])).finally(() => setLoading(false));
  useEffect(() => { load(); }, []);

  const [plans, setPlans] = useState([]);
  const [planFor, setPlanFor] = useState(null);
  const [planSwap, setPlanSwap] = useState("");
  const [planLoading, setPlanLoading] = useState(false);
  const [planSaving, setPlanSaving] = useState(false);

  const loadPlans = () => client.get("/trigger-plans").then((r) => setPlans(r.data || [])).catch(() => {});
  useEffect(() => { loadPlans(); }, []);

  const openPlan = async (trigger) => {
    setPlanFor(trigger);
    const existing = plans.find((p) => (p.trigger || "").toLowerCase() === trigger.toLowerCase());
    if (existing?.swap) { setPlanSwap(existing.swap); return; }
    setPlanLoading(true); setPlanSwap("");
    try {
      const r = await client.post("/trigger-plan/suggest", { trigger });
      setPlanSwap(r.data.swap || "");
    } catch (e) {
      setPlanSwap("");
    } finally {
      setPlanLoading(false);
    }
  };
  const savePlan = async () => {
    if (!planFor || !planSwap.trim()) return;
    setPlanSaving(true);
    try {
      await client.post("/trigger-plans", { trigger: planFor, swap: planSwap.trim() });
      toast.success("Plan saved. When this trigger hits, you'll know your move.");
      setPlanFor(null);
      loadPlans();
    } catch (e) {
      toast.error("Couldn't save that. Please try again.");
    } finally {
      setPlanSaving(false);
    }
  };

  const save = async () => {
    if (!trigger.trim()) { toast.error("Add what triggered it first."); return; }
    setSaving(true);
    try {
      await client.post("/cravings", { intensity, trigger: trigger.trim(), coped_with: coped.trim(), note: "" });
      toast.success("Logged. Naming the pattern loosens its grip.");
      setOpen(false); setIntensity(3); setTrigger(""); setCoped("");
      load();
    } catch (e) {
      toast.error("Couldn't save that. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  const counts = {};
  items.forEach((c) => { const t = (c.trigger || "").trim(); if (t) counts[t] = (counts[t] || 0) + 1; });
  const top = Object.entries(counts).sort((a, b) => b[1] - a[1]).slice(0, 4);
  const recent = items.slice(0, 4);

  return (
    <section data-testid="craving-journal">
      <div className="flex items-end justify-between gap-4 mb-4">
        <div className="flex items-center gap-2">
          <NotebookPen className="w-5 h-5 text-terracotta" />
          <h2 className="font-serif text-h2 text-olive">Craving journal</h2>
        </div>
        <button data-testid="craving-add-btn" onClick={() => setOpen((o) => !o)}
          className="inline-flex items-center gap-1.5 rounded-full bg-terracotta text-sand px-4 py-2 text-sm font-medium transition-colors hover:bg-terracotta-soft">
          {open ? <X className="w-4 h-4" /> : <Plus className="w-4 h-4" />} {open ? "Close" : "Log a craving"}
        </button>
      </div>
      <p className="text-olive/70 mb-5 max-w-2xl -mt-2">
        Jot down what set off a craving and what you did instead. Over time the patterns show — and patterns you can see, you can plan for.
      </p>

      <AnimatePresence initial={false}>
        {open && (
          <motion.div
            initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: "auto" }} exit={{ opacity: 0, height: 0 }}
            className="overflow-hidden mb-4">
            <div className="rounded-3xl bg-card border border-border p-6 shadow-soft space-y-5" data-testid="craving-form">
              <div>
                <p className="text-sm font-medium text-olive mb-2">How strong was it?</p>
                <div className="flex items-center gap-2">
                  {DOTS.map((n) => (
                    <button key={n} data-testid={`craving-intensity-${n}`} onClick={() => setIntensity(n)}
                      className={`w-10 h-10 rounded-full text-sm font-semibold transition-colors ${intensity >= n ? "bg-terracotta text-sand" : "bg-sage text-olive/60 hover:bg-sage/70"}`}>
                      {n}
                    </button>
                  ))}
                  <span className="text-xs text-stone ml-2">{intensity <= 2 ? "mild" : intensity === 3 ? "moderate" : "strong"}</span>
                </div>
              </div>

              <div>
                <p className="text-sm font-medium text-olive mb-2">What triggered it?</p>
                <div className="flex flex-wrap gap-2 mb-3">
                  {COMMON_TRIGGERS.slice(0, 8).map((t) => (
                    <button key={t} data-testid={`craving-trigger-chip-${t.replace(/[^a-z0-9]+/gi, "-").toLowerCase()}`}
                      onClick={() => setTrigger(t)}
                      className={`rounded-full px-3 py-1.5 text-xs border transition-colors ${trigger === t ? "border-terracotta bg-terracotta/5 text-terracotta" : "border-border text-olive/70 hover:bg-sage"}`}>
                      {t}
                    </button>
                  ))}
                </div>
                <Input data-testid="craving-trigger-input" value={trigger} onChange={(e) => setTrigger(e.target.value)}
                  placeholder="Or type your own trigger…" className="bg-sand" />
              </div>

              <div>
                <p className="text-sm font-medium text-olive mb-2">What did you do instead? <span className="text-stone font-normal">(optional)</span></p>
                <Input data-testid="craving-coped-input" value={coped} onChange={(e) => setCoped(e.target.value)}
                  placeholder="Prayed, drank water, went for a walk…" className="bg-sand" />
              </div>

              <Button data-testid="craving-save-btn" onClick={save} disabled={saving}
                className="w-full rounded-full bg-terracotta hover:bg-terracotta-soft text-sand gap-2">
                <Check className="w-4 h-4" /> {saving ? "Saving…" : "Save to journal"}
              </Button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {loading ? (
        <div className="animate-pulse text-stone">Opening your journal…</div>
      ) : items.length === 0 ? (
        <div className="rounded-3xl border border-dashed border-border p-7 text-center text-olive/70" data-testid="craving-empty">
          No cravings logged yet. Next time one hits, jot it here — it's not a failure, it's data.
        </div>
      ) : (
        <div className="grid lg:grid-cols-2 gap-4">
          <div className="rounded-3xl bg-card border border-border p-6 shadow-soft" data-testid="craving-patterns">
            <p className="flex items-center gap-2 font-medium text-olive mb-4"><TrendingUp className="w-4 h-4 text-sky" /> Your top triggers</p>
            <div className="space-y-3">
              {top.map(([t, c]) => {
                const max = top[0][1] || 1;
                return (
                  <div key={t} data-testid={`craving-top-${t.replace(/[^a-z0-9]+/gi, "-").toLowerCase()}`}>
                    <div className="flex items-center justify-between text-sm mb-1">
                      <span className="text-olive">{t}</span>
                      <span className="text-stone">{c}×</span>
                    </div>
                    <div className="h-2 rounded-full bg-sage overflow-hidden">
                      <div className="h-full rounded-full bg-brand-teal" style={{ width: `${Math.round((c / max) * 100)}%` }} />
                    </div>
                  </div>
                );
              })}
            </div>
            {top[0] && (
              <button data-testid="trigger-plan-btn" onClick={() => openPlan(top[0][0])}
                className="mt-5 w-full inline-flex items-center justify-center gap-2 rounded-full bg-brand-teal/10 text-brand-teal-dark py-2.5 text-sm font-medium hover:bg-brand-teal/20 transition-colors">
                <Lightbulb className="w-4 h-4" /> Make a plan for “{top[0][0]}”
              </button>
            )}
            {plans.length > 0 && (
              <div className="mt-4 space-y-2" data-testid="trigger-plans">
                {plans.map((p) => (
                  <div key={p.id} data-testid={`trigger-plan-${(p.trigger || "").replace(/[^a-z0-9]+/gi, "-").toLowerCase()}`} className="rounded-2xl bg-sage p-3">
                    <p className="text-[11px] font-semibold text-brand-teal-dark uppercase tracking-wide">Plan for {p.trigger}</p>
                    <p className="text-sm text-olive/80 mt-1 leading-relaxed">{p.swap}</p>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="rounded-3xl bg-card border border-border p-6 shadow-soft" data-testid="craving-recent">
            <p className="font-medium text-olive mb-4">Recent entries</p>
            <div className="space-y-3">
              {recent.map((c, i) => (
                <div key={c.id || i} data-testid={`craving-item-${i}`} className="flex items-start gap-3 rounded-2xl bg-sage p-3">
                  <div className="flex items-center gap-1 rounded-full bg-terracotta/10 text-terracotta px-2 py-1 text-xs font-semibold shrink-0">
                    <Flame className="w-3.5 h-3.5" /> {c.intensity}
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="text-sm text-olive font-medium truncate">{c.trigger || "A craving"}</p>
                    {c.coped_with && <p className="text-xs text-olive/70 mt-0.5">Chose: {c.coped_with}</p>}
                  </div>
                  <span className="text-xs text-stone shrink-0">{timeAgo(c.created_at)}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      <ResponsiveModal open={!!planFor} onOpenChange={(o) => !o && setPlanFor(null)} testid="trigger-plan-modal"
        icon={<div className="rounded-xl bg-brand-teal/15 p-2 shrink-0"><Lightbulb className="w-4 h-4 text-brand-teal-dark" /></div>}
        title={planFor ? `Your plan for “${planFor}”` : "Your plan"}
        description="A ready-made swap for the next time this trigger hits. Make it yours, then save it."
        footer={
          <div className="flex flex-col sm:flex-row gap-2.5">
            <Button variant="outline" data-testid="trigger-plan-cancel" onClick={() => setPlanFor(null)} className="flex-1 rounded-full border-olive/20 hover:bg-sage">Not now</Button>
            <Button data-testid="trigger-plan-save" onClick={savePlan} disabled={planSaving || planLoading || !planSwap.trim()} className="flex-1 rounded-full bg-terracotta hover:bg-terracotta-soft text-sand">
              {planSaving ? "Saving…" : "Save my plan"}
            </Button>
          </div>
        }>
        <div className="pb-2">
          {planLoading ? (
            <div className="animate-pulse text-stone py-8 text-center" data-testid="trigger-plan-loading">Thinking of a swap for you…</div>
          ) : (
            <Textarea data-testid="trigger-plan-swap" value={planSwap} onChange={(e) => setPlanSwap(e.target.value)} rows={6}
              className="bg-sand" placeholder="What will you do instead when this trigger hits?" />
          )}
        </div>
      </ResponsiveModal>
    </section>
  );
}
